// Globe-icon language switcher dropdown for the top nav.
// Persists choice via i18next-browser-languagedetector → localStorage.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "@phosphor-icons/react";
import { SUPPORTED_LOCALES } from "../i18n";

export function LanguageSwitcher({ compact = false }) {
    const { i18n, t } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onDoc(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const current = SUPPORTED_LOCALES.find((l) => l.code === i18n.resolvedLanguage)
        || SUPPORTED_LOCALES.find((l) => l.code === i18n.language)
        || SUPPORTED_LOCALES[0];

    function pick(code) {
        i18n.changeLanguage(code);
        setOpen(false);
    }

    return (
        <div ref={ref} className="relative" data-testid="lang-switcher">
            <button
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm text-[#5C5A59] hover:text-[#6B7DB8] hover:bg-[#F4F6FB] transition-colors"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={t("nav.language")}
                data-testid="lang-switcher-button"
            >
                <Globe size={16} weight="bold" />
                <span className="hidden sm:inline">{current.native}</span>
                {compact && <span className="sm:hidden uppercase text-xs">{current.code}</span>}
            </button>
            {open && (
                <div
                    className="absolute right-0 mt-2 w-52 rounded-2xl bg-white border border-[#E5E1D8] shadow-rem-card py-2 max-h-[80vh] overflow-y-auto z-50"
                    role="listbox"
                    data-testid="lang-switcher-menu"
                >
                    {SUPPORTED_LOCALES.map((l) => (
                        <button
                            key={l.code}
                            onClick={() => pick(l.code)}
                            className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-[#F4F6FB] transition-colors ${l.code === current.code ? "text-[#4A5A95] font-medium" : "text-[#2D2B2A]"}`}
                            data-testid={`lang-option-${l.code}`}
                            role="option"
                            aria-selected={l.code === current.code}
                        >
                            <span>
                                <span className="font-medium">{l.native}</span>
                                {l.native !== l.label && (
                                    <span className="ml-2 text-xs text-[#8A8786]">{l.label}</span>
                                )}
                            </span>
                            {l.code === current.code && <Check size={14} weight="bold" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
